import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import './App.css';
import Login from './screens/Login/Login';
import VersionList from './screens/VersionList/VersionList';
import Editor from './screens/Editor/Editor';
import CreateFestival from './screens/Admin/CreateFestival/CreateFestival';
import Admin from './screens/Admin/Admin/Admin';
import CreateUser from './screens/Admin/CreateUser/CreateUser';
import FestivalsList from './screens/Admin/FestivalsList/FestivalsList';
import Cookies from 'js-cookie';
import { Session } from './model/Session';
import { User } from './model/User';
import { EditorType, Role } from './model/Roles';
import ResetPassword from './screens/Login/ResetPassword';
import Migrations from './screens/Admin/Migrations/Migrations';
import UpdateFirebaseSecret from './screens/Admin/UpdateFirebaseSecret/UpdateFirebaseSecret';
import { logout } from './services/api';
import { SESSION_COOKIE_ATTRIBUTES, readSession } from './services/sessionCookie';

const editorTabs: EditorType[] = [
  'infos',
  'map',
  'tickets',
  'news',
  'artists',
  'pois',
  'shows',
  'notifications',
  'users',
  'history',
  'carpools',
  'reporting',
  'pages',
  'advertisements'
];

console.log('API Base URL:', process.env.REACT_APP_API_BASE_URL);

const App: React.FC = () => {
  // Read before the first render, so a signed-in reload goes straight to its screen
  const [session, setSession] = useState<Session | null>(readSession);

  const handleLogin = (token: string, festivalId: string, user: User) => {
    const newSession: Session = { token, festivalId, user };
    setSession(newSession);
    Cookies.set('session', JSON.stringify(newSession), SESSION_COOKIE_ATTRIBUTES);
  };

  const handleLogout = () => {
    if (session) {
      // Revokes the token server side; the local logout doesn't wait for it
      logout(session.token, session.festivalId).catch(error => console.error('Failed to revoke session:', error));
    }
    setSession(null);
    Cookies.remove('session');
  };

  const handleFestivalCreated = (newFestivalId: string, adminEmail: string) => {
    window.location.href = `/?festival_id=${newFestivalId}`;
  };

  return (
    <Router>
      <Switch>
        <Route exact path="/">
          {session ? (
            <VersionList
              sessionToken={session.token}
              festivalId={session.festivalId}
              canCreateDraft={session.user.roles.includes(Role.ADMIN)}
              onLogout={handleLogout}
            />
          ) : (
            <Login onLogin={handleLogin} />
          )}
        </Route>
        <Route exact path="/admin">
          <Admin />
        </Route>
        <Route path="/admin/create-festival">
          <CreateFestival
            onBack={() => window.location.href = '/admin'}
            onFestivalCreated={handleFestivalCreated}
          />
        </Route>
        <Route path="/admin/create-user">
          <CreateUser />
        </Route>
        <Route path="/admin/festivals-list">
          <FestivalsList />
        </Route>
        <Route path="/admin/migrations">
          <Migrations />
        </Route>
        <Route path="/admin/update-firebase-secret">
          <UpdateFirebaseSecret />
        </Route>
        <Route path="/reset-password">
          <ResetPassword />
        </Route>
        <Route
          path={`/editor/:versionId/:tab(${editorTabs.join('|')})`}
          render={({ match }) => session ? (
            // One editor per version: switching tabs keeps it, with its data, sidebar and preview
            <Editor
              key={match.params.versionId}
              sessionToken={session.token}
              festivalId={session.festivalId}
              currentUser={session.user}
              tab={match.params.tab as EditorType}
            />
          ) : (
            <Redirect to={{ pathname: '/', state: { from: window.location.pathname } }} />
          )}
        />
        <Route
          path="/editor/:versionId"
          render={({ match }) => (
            <Redirect to={session ? `/editor/${match.params.versionId}/infos` : '/'} />
          )}
        />
      </Switch>
    </Router>
  );
};

export default App;
